import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';

// material-ui
import { Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';

// third-party
import Chart from 'react-apexcharts';

// project imports
import SkeletonTotalGrowthBarChart from 'ui-component/cards/Skeleton/TotalGrowthBarChart';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import NumberFormat from 'react-number-format';
import axios from 'axios';
import ApiLink from 'API';
import { useNavigate } from 'react-router';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function numberWithCommas(x) {
    if (x === null || x === undefined) return '0';
    return Number(x).toLocaleString('en-US');
}

const TotalGrowthBarChart = ({ cusId }) => {
    const now = useMemo(() => new Date(), []);
    const [mode, setMode] = useState('year'); // 'month' | 'year'
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1); // 1..12
    const [isLoading, setisLoading] = useState(true);
    const [Data, setData] = useState([]);
    const navigate = useNavigate();

    // Build server param: 'YYYY-MM' for month-mode, 'YYYY' for year-mode
    const monthYearParam = useMemo(
        () => (mode === 'month' ? `${year}-${String(month).padStart(2, '0')}` : `${year}`),
        [mode, year, month]
    );

    // Year options: current year back to 9 years ago
    const yearOptions = useMemo(() => {
        const arr = [];
        const cur = now.getFullYear();
        for (let y = cur; y >= cur - 9; y--) arr.push(y);
        return arr;
    }, [now]);

    useEffect(() => {
        let isMounted = true;
        const LoadData = async () => {
            try {
                setisLoading(true);
                const response = await axios.get(ApiLink + '/api/stockcustomer/customer/chartsale/' + monthYearParam, {
                    withCredentials: true,
                    headers: { token: localStorage.getItem('token') }
                });
                if (isMounted) {
                    if (response.status === 200) {
                        setData(response.data.data || []);
                        setisLoading(false);
                    } else if (response.status === 401) {
                        localStorage.clear();
                        navigate(0);
                    }
                }
            } catch (error) {
                if (isMounted) {
                    setisLoading(false);
                    if (error?.response?.status === 401) {
                        localStorage.clear();
                        navigate(0);
                    }
                }
            }
        };
        LoadData();
        return () => {
            isMounted = false;
        };
    }, [cusId, monthYearParam]);

    const chartData = useMemo(() => {
        let categories = [];
        let Income = [];
        let Investment = [];
        let Profit = [];
        const safeData = Data || [];
        for (let i = 0; i < safeData.length; i++) {
            const item = safeData[i];
            categories.push(`${item._id?.year}-${item._id?.month}${mode === 'month' ? `-${item._id?.day}` : ''}`);
            Profit.push(Number(item.salePrice || 0) - Number(item.importPrice || 0));
            Investment.push(Number(item.importPrice || 0));
            Income.push(Number(item.salePrice || 0));
        }
        return {
            categories,
            series: [
                { name: 'Income₭', data: Income },
                { name: 'Investment₭', data: Investment },
                { name: 'Profit₭', data: Profit }
            ]
        };
    }, [Data, mode]);

    const chartOptions = useMemo(
        () => ({
            chart: {
                id: 'customer-bar-chart',
                type: 'bar',
                height: 480,
                stacked: false,
                toolbar: { show: true },
                zoom: { enabled: true },
                animations: { enabled: false }
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '50%',
                    dataLabels: {
                        position: 'center',
                        orientation: 'vertical'
                    }
                }
            },
            xaxis: {
                type: 'category',
                categories: chartData.categories
            },
            legend: {
                show: true,
                position: 'bottom'
            },
            dataLabels: {
                enabled: true,
                formatter: (val) => numberWithCommas(val),
                style: {
                    fontSize: '12px',
                    fontWeight: 'bold',
                    colors: ['#fff', '#fff', '#333']
                },
                offsetY: 0
            },
            grid: { show: true },
            yaxis: {
                labels: {
                    formatter: (val) => numberWithCommas(val)
                }
            },
            tooltip: {
                y: {
                    formatter: (val) => `${numberWithCommas(val)} ₭`
                }
            }
        }),
        [chartData.categories]
    );

    const totalIncomeValue = useMemo(() => {
        let sum = 0;
        const safeData = Data || [];
        for (let i = 0; i < safeData.length; i++) {
            sum += Number(safeData[i].salePrice || 0);
        }
        return sum;
    }, [Data]);

    return (
        <>
            {isLoading ? (
                <SkeletonTotalGrowthBarChart />
            ) : (
                <MainCard>
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <Grid container alignItems="center" justifyContent="space-between">
                                <Grid item>
                                    <Grid container direction="column" spacing={1}>
                                        <Grid item>
                                            <Typography variant="subtitle2">Total Income</Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="h3">
                                                <NumberFormat
                                                    thousandsGroupStyle="thousand"
                                                    value={totalIncomeValue}
                                                    prefix="₭"
                                                    decimalSeparator="."
                                                    displayType="text"
                                                    thousandSeparator={true}
                                                />
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ paddingTop: 2 }}>
                                        <TextField
                                            select
                                            size="small"
                                            label="Filter"
                                            value={mode}
                                            onChange={(e) => setMode(e.target.value)}
                                            sx={{ minWidth: 80 }}
                                        >
                                            <MenuItem value="month">Month</MenuItem>
                                            <MenuItem value="year">Year</MenuItem>
                                        </TextField>
                                        <TextField
                                            select
                                            size="small"
                                            label="Year"
                                            value={year}
                                            onChange={(e) => setYear(Number(e.target.value))}
                                            sx={{ minWidth: 80 }}
                                        >
                                            {yearOptions.map((y) => (
                                                <MenuItem key={y} value={y}>
                                                    {y}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        {mode === 'month' && (
                                            <TextField
                                                select
                                                size="small"
                                                label="Month"
                                                value={month}
                                                onChange={(e) => setMonth(Number(e.target.value))}
                                                sx={{ minWidth: 100 }}
                                            >
                                                {MONTH_NAMES.map((name, idx) => (
                                                    <MenuItem key={idx + 1} value={idx + 1}>
                                                        {name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Chart options={chartOptions} series={chartData.series} type="bar" height={480} />
                        </Grid>
                    </Grid>
                </MainCard>
            )}
        </>
    );
};

TotalGrowthBarChart.propTypes = {
    isLoading: PropTypes.bool,
    cusId: PropTypes.any
};

export default TotalGrowthBarChart;
